import {
  ContactFileState,
  contactFilesForContactIdSelector,
  contactFilesForEmployeeIdSelector,
} from './contact-file-selectors';
import { FAILURE, STARTING, SUCCESS } from '../action/util/action-name-creators';
import {
  Action,
  StateStatus,
  deleteResourceByParam,
  setStateDirtyByParam,
  updateResourceByParam,
} from '../reducers/utils/state-manager';

import { convertArrayToMap } from '../util/convert-array-to-map';

const InitialState: ContactFileState = {
  contactFilesById: {},
  contactFilesStateByContactId: {},
  presignedUrlsById: {},
  presignedUrlsStateByContactFileIds: {},
};

const setStateDirtyForContactId = (state: ContactFileState, contactId: string) => ({
  ...setStateDirtyByParam(
    state,
    'presignedUrls',
    'contactFileIds',
    JSON.stringify(state.contactFilesById[contactId as any]),
  ),
  ...setStateDirtyByParam(state, 'contactFiles', 'contactId', contactId.toString()),
});

export const contactFileReducer = (state = InitialState, action: Action) => {
  switch (action.type) {
    case 'SET_DIRTY_ON_TAB_REGAIN_VISIBILITY': {
      return Object.keys(state.contactFilesStateByContactId).reduce(
        (acc, contactId) => ({
          ...acc,
          ...setStateDirtyForContactId(acc, contactId),
        }),
        state,
      );
    }
    case STARTING('LOAD_CONTACT_MESSAGES'): {
      const { contactId } = action.payload.args;
      return setStateDirtyForContactId(state, contactId);
    }
    case STARTING('LOAD_CONTACT_FILES'): {
      const { contactId } = action.payload.args;
      return updateResourceByParam(state, 'contactFiles', 'contactId', contactId, true);
    }
    case SUCCESS('LOAD_CONTACT_FILES'): {
      const {
        args: { contactId },
        data,
      } = action.payload;
      return updateResourceByParam(
        state,
        'contactFiles',
        'contactId',
        contactId,
        false,
        convertArrayToMap(data, (d: ContactFile) => String(d.contactFileId)),
      );
    }
    case FAILURE('LOAD_CONTACT_FILES'): {
      const { contactId } = action.payload.args;
      return updateResourceByParam(state, 'contactFiles', 'contactId', contactId, false);
    }
    case SUCCESS('CREATE_CONTACT_FILE'): {
      const { data } = action.payload;
      const { contactFileId, contactId, fileName } = data;
      let newState = { ...state };

      // IF (12/10/20): If there is already a file with this name for the same contact,
      // that means it was overwritten, so delete the one currently in the store before adding the new one
      const existingContactFile = contactFilesForContactIdSelector(
        { contactFiles: state },
        contactId,
      ).find((f) => f.fileName === fileName);
      if (existingContactFile) {
        newState = deleteResourceByParam(
          state,
          'contactFiles',
          'contactId',
          contactId,
          existingContactFile.contactFileId.toString(),
        );
      }

      return updateResourceByParam(newState, 'contactFiles', 'contactId', contactId, false, {
        [contactFileId]: data,
      });
    }
    case SUCCESS('DELETE_CONTACT_FILE'): {
      const { contactId, contactFileId } = action.payload.data;
      return deleteResourceByParam(state, 'contactFiles', 'contactId', contactId, contactFileId);
    }
    case SUCCESS('DELETE_EMPLOYEE'): {
      const { employeeId } = action.payload.args;
      const newState = state;
      contactFilesForEmployeeIdSelector({ contactFiles: state }, employeeId).forEach(
        ({ contactFileId }) => {
          delete newState.contactFilesById[contactFileId];
        },
      );
      return newState;
    }
    case STARTING('LOAD_CONTACT_FILE_PRESIGNED_URLS'): {
      const { contactFileIds } = action.payload.args;
      return updateResourceByParam(
        state,
        'presignedUrls',
        'contactFileIds',
        JSON.stringify(contactFileIds),
        true,
      );
    }
    case SUCCESS('LOAD_CONTACT_FILE_PRESIGNED_URLS'): {
      const { contactFileIds } = action.payload.args;
      const { presignedUrls } = action.payload.data;
      const data = presignedUrls.reduce(
        (
          acc: { [contactFileId: number]: string },
          {
            contactFileId,
            presignedUrl,
            expiresAt,
          }: { contactFileId: number; presignedUrl: string; expiresAt: string },
        ) => ({
          ...acc,
          [contactFileId]: {
            presignedUrl,
            expiresAt,
          },
        }),
        {},
      );

      return {
        ...state,
        presignedUrlsById: { ...state.presignedUrlsById, ...data },
        presignedUrlsStateByContactFileIds: {
          ...state.presignedUrlsStateByContactFileIds,
          [JSON.stringify(contactFileIds)]: StateStatus.success,
        },
      };
    }
    case FAILURE('LOAD_CONTACT_FILE_PRESIGNED_URLS'): {
      const { contactFileIds } = action.payload.args;
      return updateResourceByParam(
        state,
        'presignedUrls',
        'contactFileIds',
        JSON.stringify(contactFileIds),
        false,
      );
    }
    default: {
      return state;
    }
  }
};
